import { useRef, useEffect } from 'react';
import { Loader2, Send } from 'lucide-react';

interface Message {
  id: string;
  senderId: string;
  senderType: 'warehouse' | 'carrier' | 'system';
  content: string;
  createdAt: string;
  readAt: string | null;
}

interface MessagesTabContentProps {
  messages: Message[];
  userId: string | undefined;
  newMessage: string;
  isSending: boolean;
  onNewMessageChange: (value: string) => void;
  onSendMessage: () => void;
  formatTime: (dateStr?: string) => string;
}

export function MessagesTabContent({
  messages,
  userId,
  newMessage,
  isSending,
  onNewMessageChange,
  onSendMessage,
  formatTime,
}: MessagesTabContentProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex flex-col h-full max-h-[500px]">
      <div className="flex-1 overflow-y-auto space-y-4 pr-2 mb-4">
        {messages?.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">No messages yet.</p>
        ) : (
          messages?.map((msg) => (
            <div key={msg.id} className={`flex ${msg.senderId === userId ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-lg p-3 text-sm ${msg.senderId === userId
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                  }`}
              >
                {msg.senderType === 'system' && (
                  <span className="text-xs uppercase font-bold opacity-70 block mb-1">System</span>
                )}
                <p>{msg.content}</p>
                <div className={`text-xs mt-1 text-right ${msg.senderId === userId ? 'text-blue-100' : 'text-slate-500'}`}>
                  {formatTime(msg.createdAt)}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => onNewMessageChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !isSending && onSendMessage()}
          placeholder="Type a message..."
          className="flex-1 field"
        />
        <button onClick={onSendMessage} disabled={!newMessage.trim() || isSending} className="btn btn-primary">
          {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
